/**
 * runidea.ai — Phase 14 Wave 2 — Typed-confirm dialog.
 *
 * Generic confirmation dialog that requires the user to type a magic word
 * (default "DELETE") before the destructive button activates. Used for
 * cross-user delete + bulk-delete to make accidents almost impossible.
 */
import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  magicWord = "DELETE",
  confirmLabel = "Confirm",
  onConfirm,
  loading = false,
  testid = "typed-confirm",
}) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!open) setText("");
  }, [open]);
  const enabled = text.trim() === magicWord && !loading;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`${testid}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> {title || "Confirm destructive action"}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className="text-sm text-slate-600">
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <label className="text-xs font-medium text-slate-700">
            Type <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-red-700">{magicWord}</span> to confirm.
          </label>
          <Input
            data-testid={`${testid}-input`}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={magicWord}
            className="font-mono"
          />
        </div>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!enabled}
            onClick={onConfirm}
            data-testid={`${testid}-confirm`}
          >
            {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
